import React, { useContext } from 'react';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { FilterBarInternalContext } from '../state/FilterBarContext';
import type {
  DateRangeValue,
  FilterBarController,
  FilterItemConfig,
  FilterValues,
  NumericRangeValue,
} from '../types';
import type { Dayjs } from 'dayjs';

import cn from './styles.module.scss';

export interface FilterSummaryProps<T extends FilterValues = FilterValues> {
  controller?: FilterBarController<T>;
  className?: string;
  /** Hide the "Clear all" affordance. Default: false (shown). */
  hideClearAll?: boolean;
}

/**
 * Chip-row summary component.
 *
 * Plan Section "Optional `FilterSummary` chip row":
 *   - Iterates `items` config + `values`.
 *   - For each non-empty field, renders one or more chips (multi-select → N chips).
 *   - Each chip has an X that calls `controller.set(name, ...)` to remove that value.
 *   - "Clear All" calls `controller.clearAll()`.
 *   - Omitted entirely if `itemConfig.excludeFromSummary === true`.
 */
export function FilterSummary<T extends FilterValues = FilterValues>(props: FilterSummaryProps<T>) {
  const { className, hideClearAll } = props;
  // Read from internal context (when nested inside FilterBar); fallback to prop controller.
  const ctx = useContext(FilterBarInternalContext);
  const controller = (props.controller ?? ctx?.controller) as FilterBarController<T> | undefined;
  if (!controller) {
    throw new Error('[FilterSummary] must receive a controller prop or be rendered inside <FilterBar>.');
  }
  const labels = ctx?.labels;
  // Prefer context's items map; fall back to controller.items (for standalone usage).
  const itemsByName = ctx?.itemsByName ?? buildItemsByName(controller.items);

  const chips: React.ReactNode[] = [];
  for (const name of Object.keys(controller.values)) {
    const config = itemsByName[name];
    if (!config || config.excludeFromSummary) continue;
    const value = (controller.values as FilterValues)[name];
    const fieldChips = renderChips(config, value, controller, name);
    chips.push(...fieldChips);
  }

  if (chips.length === 0) return null;

  return (
    <div className={classNames(cn.summary, className)} data-testid="filter-summary">
      {labels && <span className={cn.summaryLabel}>{labels.filtersLabel}</span>}
      {chips}
      {!hideClearAll && (
        <button
          type="button"
          className={cn.clearAllButton}
          onClick={() => controller.clearAll()}
          data-testid="filter-summary-clear-all"
        >
          {labels?.clearAll ?? 'Clear All'}
        </button>
      )}
    </div>
  );
}

function renderChips<T extends FilterValues>(
  config: FilterItemConfig,
  value: unknown,
  controller: FilterBarController<T>,
  name: string,
): React.ReactNode[] {
  if (isEmpty(value)) return [];

  if (config.type === 'SELECT_MULTI') {
    const arr = value as Array<string | number>;
    return arr.map((v) => {
      const opt = controller.findOption(name, v);
      const label = opt?.label ?? String(v);
      return (
        <ChipButton
          key={`${name}-${v}`}
          name={name}
          label={`${config.label}: ${label}`}
          onRemove={() => {
            const next = (controller.values[name] as Array<string | number>).filter((x) => x !== v);
            controller.set(name, next);
          }}
        />
      );
    });
  }

  if (config.type === 'SELECT') {
    const opt = controller.findOption(name, value);
    return [
      <ChipButton
        key={name}
        name={name}
        label={`${config.label}: ${String(opt?.label ?? value)}`}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  if (config.type === 'TEXT_SEARCH') {
    return [
      <ChipButton
        key={name}
        name={name}
        label={`${config.label}: "${value}"`}
        onRemove={() => controller.set(name, '')}
      />,
    ];
  }

  if (config.type === 'SEGMENT_TOGGLE') {
    // Reads from the loaded options (dataSource — static or async) via the
    // controller, uniform with SELECT.
    const opt = controller.findOption(name, value);
    return [
      <ChipButton
        key={name}
        name={name}
        label={`${config.label}: ${String(opt?.label ?? value)}`}
        removable={false}
      />,
    ];
  }

  if (config.type === 'DATE_RANGE') {
    const pair = value as DateRangeValue;
    if (!pair) return [];
    const [a, b] = pair;
    const fmt = config.format ?? 'YYYY-MM-DD';
    const aStr = a ? (a as Dayjs).format(fmt) : '…';
    const bStr = b ? (b as Dayjs).format(fmt) : '…';
    return [
      <ChipButton
        key={name}
        name={name}
        label={`${config.label}: ${aStr} – ${bStr}`}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  if (config.type === 'NUMERIC_RANGE') {
    const pair = value as NumericRangeValue;
    if (!pair) return [];
    const [a, b] = pair;
    const fmt = (n: number | null) => {
      if (n === null) return '…';
      if (config.format === 'currency') return `${config.currency ?? '$'}${n}`;
      if (config.format === 'percent') return `${n}%`;
      return String(n);
    };
    return [
      <ChipButton
        key={name}
        name={name}
        label={`${config.label}: ${fmt(a)} – ${fmt(b)}`}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  return [];
}

interface ChipButtonProps {
  name: string;
  label: string;
  onRemove?: () => void;
  removable?: boolean;
}

const ChipButton: React.FC<ChipButtonProps> = ({ name, label, onRemove, removable = true }) => (
  <span
    className={cn.summaryChip}
    data-testid={`filter-summary-chip-${name}`}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '2px 8px',
      borderRadius: 12,
      background: 'var(--bc-color-fill-tertiary, #F6F6F3)',
      color: 'var(--bc-color-text, #17191A)',
      fontSize: 12,
    }}
  >
    {label}
    {removable && onRemove && (
      <button
        type="button"
        aria-label="Remove filter chip"
        onClick={onRemove}
        className={cn.summaryChipRemove}
        data-testid={`filter-summary-chip-remove-${name}`}
      >
        <CloseOutlined style={{ fontSize: 10 }} />
      </button>
    )}
  </span>
);

function buildItemsByName(items: FilterItemConfig[]): Record<string, FilterItemConfig> {
  const out: Record<string, FilterItemConfig> = {};
  for (const item of items) out[item.name] = item;
  return out;
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === 'string' && v === '') return true;
  return false;
}
